<template>
    <div class="max-w-7xl mx-auto space-y-6 py-6 px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-white">
            Payment Methods
          </h1>
          <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Manage your payment methods for automatic billing
          </p>
        </div>
        <div class="flex items-center space-x-3">
          <Link :href="route('settings.billing.index')">
            <Button variant="secondary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Billing
            </Button>
          </Link>
          <Link :href="route('settings.billing.payment-methods.create')">
            <Button>
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              Add Payment Method
            </Button>
          </Link>
        </div>
      </div>

      <!-- Error Message -->
      <Alert v-if="error" variant="danger">
        {{ error }}
      </Alert>

      <!-- Active Subscription Warning -->
      <Alert v-if="hasActiveSubscription && paymentMethods.length === 0" variant="warning">
        <template #title>Payment Method Required</template>
        You have an active subscription but no payment method on file. Please add a payment method to avoid service interruption.
      </Alert>

      <!-- Payment Methods List -->
      <div v-if="paymentMethods.length > 0" class="space-y-4">
        <Card
          v-for="method in paymentMethods"
          :key="method.id"
          class="relative"
          :class="{
            'ring-2 ring-blue-500': method.is_default,
            'opacity-75': method.is_expired,
          }"
        >
          <div class="flex items-center justify-between">
            <!-- Card Info -->
            <div class="flex items-center space-x-4">
              <!-- Card Brand Logo -->
              <div class="flex-shrink-0">
                <div
                  class="w-14 h-10 rounded border border-gray-200 dark:border-gray-700 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900"
                >
                  <component
                    :is="getCardIcon(method.card_brand)"
                    class="w-8 h-8"
                  />
                </div>
              </div>

              <!-- Card Details -->
              <div>
                <div class="flex items-center space-x-2">
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-white">
                    {{ method.display_name }}
                  </h3>
                  <span
                    v-if="method.is_default"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                  >
                    Default
                  </span>
                  <span
                    v-if="method.is_expired"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                  >
                    Expired
                  </span>
                </div>
                <p class="text-sm text-gray-600 dark:text-gray-400">
                  Expires {{ method.expiration_date }}
                </p>
                <p class="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  Added {{ method.created_at }}
                </p>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex items-center space-x-2">
              <!-- Set as Default Button -->
              <Button
                v-if="!method.is_default"
                variant="secondary"
                size="sm"
                @click="setAsDefault(method.id)"
                :disabled="processingMethod === method.id"
              >
                Set as Default
              </Button>

              <!-- Remove Button -->
              <Button
                variant="danger"
                size="sm"
                @click="confirmRemove(method)"
                :disabled="processingMethod === method.id || (hasActiveSubscription && paymentMethods.length === 1)"
                :title="hasActiveSubscription && paymentMethods.length === 1 ? 'Cannot remove the only payment method with an active subscription' : 'Remove payment method'"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <!-- Empty State -->
      <Card v-else class="text-center py-12">
        <div class="flex flex-col items-center">
          <div class="w-20 h-20 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
            <svg class="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900 dark:text-white dark:text-white mb-2">
            No Payment Methods
          </h3>
          <p class="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
            You haven't added any payment methods yet. Add a credit or debit card to enable automatic billing.
          </p>
          <Link :href="route('settings.billing.payment-methods.create')">
            <Button>
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              Add Your First Payment Method
            </Button>
          </Link>
        </div>
      </Card>

      <!-- Help Text -->
      <Card v-if="paymentMethods.length > 0" variant="muted">
        <div class="flex items-start space-x-3">
          <svg class="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div class="text-sm text-gray-600 dark:text-gray-400">
            <p class="font-semibold text-gray-900 dark:text-white mb-1">About Payment Methods</p>
            <ul class="list-disc list-inside space-y-1">
              <li>Your default payment method will be used for automatic billing</li>
              <li>All payment information is securely stored with Stripe</li>
              <li>You can update or remove payment methods at any time</li>
              <li v-if="hasActiveSubscription">You must have at least one payment method with an active subscription</li>
            </ul>
          </div>
        </div>
      </Card>
    </div>

    <!-- Confirmation Modal -->
    <ConfirmModal
      :show="showRemoveModal"
      title="Remove Payment Method"
      :message="`Are you sure you want to remove ${methodToRemove?.display_name}? This action cannot be undone.`"
      confirm-text="Remove"
      confirm-variant="danger"
      @confirm="removePaymentMethod"
      @cancel="cancelRemove"
    />
</template>

<script setup>
import { ref, h } from 'vue';
import { Link, router } from '@inertiajs/vue3';
import Card from '@/Components/Common/Card.vue';
import Button from '@/Components/Common/Button.vue';
import Alert from '@/Components/Common/Alert.vue';
import ConfirmModal from '@/Components/Common/ConfirmModal.vue';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';

// Props
const props = defineProps({
  paymentMethods: {
    type: Array,
    default: () => [],
  },
  hasActiveSubscription: {
    type: Boolean,
    default: false,
  },
  company: {
    type: Object,
    required: true,
  },
  error: {
    type: String,
    default: null,
  },
});

// Composables
const { success, error: showError } = useToast();

// Refs
const processingMethod = ref(null);
const showRemoveModal = ref(false);
const methodToRemove = ref(null);

// Card Brand Icons
const getCardIcon = (brand) => {
  const brandIcons = {
    visa: () => h('svg', {
      viewBox: '0 0 48 32',
      fill: 'currentColor',
      class: 'text-blue-600'
    }, [
      h('path', { d: 'M20.925 14.13l-1.424 7.003h-2.278l1.423-7.003h2.279zm10.876-.446c-.453-.18-1.16-.374-2.045-.374-2.254 0-3.843 1.135-3.855 2.762-.01 1.202 1.135 1.872 2.002 2.272.889.41 1.187.673 1.183 1.04-.007.561-.71.817-1.367.817-0.914 0-1.399-.126-2.149-.438l-.294-.134-.32 1.874c.534.234 1.52.437 2.546.447 2.395 0 3.954-1.12 3.968-2.854.007-0.952-.599-1.675-1.913-2.272-.796-.387-1.284-.645-1.279-1.037 0-.348.414-.72 1.31-.72.748-.012 1.29.151 1.712.32l.205.097.297-1.74z' })
    ]),
    mastercard: () => h('svg', {
      viewBox: '0 0 48 32',
      fill: 'none',
      class: 'text-red-500'
    }, [
      h('circle', { cx: '18', cy: '16', r: '10', fill: '#EB001B' }),
      h('circle', { cx: '30', cy: '16', r: '10', fill: '#F79E1B' }),
      h('path', { d: 'M24 8c-2.21 0-4.21.9-5.66 2.34C19.79 11.79 20.79 13.79 21.66 16c-.87 2.21-1.87 4.21-3.32 5.66C19.79 23.1 21.79 24 24 24c2.21 0 4.21-.9 5.66-2.34C28.21 20.21 27.21 18.21 26.34 16c.87-2.21 1.87-4.21 3.32-5.66C28.21 8.9 26.21 8 24 8z', fill: '#FF5F00' })
    ]),
    amex: () => h('svg', {
      viewBox: '0 0 48 32',
      fill: 'currentColor',
      class: 'text-blue-400'
    }, [
      h('rect', { width: '48', height: '32', rx: '4', fill: '#016FD0' }),
      h('text', { x: '24', y: '20', 'text-anchor': 'middle', fill: 'white', 'font-size': '10', 'font-weight': 'bold' }, 'AMEX')
    ]),
    discover: () => h('svg', {
      viewBox: '0 0 48 32',
      fill: 'currentColor',
      class: 'text-orange-500'
    }, [
      h('rect', { width: '48', height: '32', rx: '4', fill: '#FF6000' }),
      h('circle', { cx: '40', cy: '16', r: '8', fill: '#FFB300' })
    ]),
  };

  return brandIcons[brand?.toLowerCase()] || (() => h('svg', {
    viewBox: '0 0 48 32',
    fill: 'none',
    stroke: 'currentColor',
    class: 'text-gray-400'
  }, [
    h('rect', { x: '4', y: '8', width: '40', height: '16', rx: '2', 'stroke-width': '2' }),
    h('line', { x1: '4', y1: '14', x2: '44', y2: '14', 'stroke-width': '2' })
  ]));
};

// Set payment method as default
const setAsDefault = (methodId) => {
  processingMethod.value = methodId;

  router.patch(
    route('settings.billing.payment-methods.set-default', methodId),
    {
      _company_id: props.company.id, // Required for company context validation
    },
    {
      preserveScroll: true,
      onSuccess: () => {
        success('Default payment method updated');
      },
      onError: () => {
        showError('Failed to update default payment method');
      },
      onFinish: () => {
        processingMethod.value = null;
      },
    }
  );
};

// Confirm remove payment method
const confirmRemove = (method) => {
  methodToRemove.value = method;
  showRemoveModal.value = true;
};

// Cancel remove
const cancelRemove = () => {
  methodToRemove.value = null;
  showRemoveModal.value = false;
};

// Remove payment method
const removePaymentMethod = () => {
  if (!methodToRemove.value) return;

  processingMethod.value = methodToRemove.value.id;

  router.delete(
    route('settings.billing.payment-methods.destroy', methodToRemove.value.id),
    {
      data: {
        _company_id: props.company.id, // Required for company context validation
      },
      preserveScroll: true,
      onSuccess: () => {
        success('Payment method removed successfully');
        showRemoveModal.value = false;
        methodToRemove.value = null;
      },
      onError: () => {
        showError('Failed to remove payment method');
      },
      onFinish: () => {
        processingMethod.value = null;
      },
    }
  );
};
</script>
